import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Row, Col, Table, Input, Select, Dropdown as AntDropdown, Empty } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { Spinner } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import IconButton from "components/common/IconButton";
import { useAuth } from "contexts/AuthContext";
import useBusinessGrowthPartners from "hooks/finance/useBusinessGrowthPartners";
import BusinessGrowthPartnerDrawer from "./BusinessGrowthPartnerDrawer";
import BusinessGrowthPartnerDetailDrawer from "./BusinessGrowthPartnerDetailDrawer";

const formatDateLong = (dateValue) => {
  if (!dateValue) return "-";
  try {
    const d = new Date(dateValue);
    if (isNaN(d)) return "-";
    const datePart = d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const timePart = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart}, ${timePart.toLowerCase()}`;
  } catch {
    return "-";
  }
};

const { TextArea } = Input;

const STATUS_FILTER_OPTIONS = [
  { label: "All Statuses", value: "ALL" },
  { label: "Pending", value: "PENDING" },
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const STATUS_BADGE_COLORS = {
  PENDING: "warning",
  ACTIVE: "success",
  INACTIVE: "secondary",
  SUSPENDED: "warning",
  CANCELLED: "danger",
};

const PARTNER_TYPE_LABEL = {
  USER: "User",
  ORGANIZATION: "Organization",
};

const computeStats = (partners) => {
  const statuses = ["PENDING", "ACTIVE", "INACTIVE", "SUSPENDED", "CANCELLED"];
  const counts = statuses.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
  if (!partners) return counts;
  partners.forEach((p) => {
    const status = p.status || "UNKNOWN";
    if (counts[status] !== undefined) counts[status]++;
  });
  return counts;
};

export default function BusinessGrowthPartnersListPage() {
  const { user } = useAuth();
  const actorId = user?.id || "";

  const { partners, loading, error, page, list, create, update, cancel } =
    useBusinessGrowthPartners();

  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Cancel confirmation state
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Status change state
  const [statusChangeTarget, setStatusChangeTarget] = useState(null);
  const [selectedNewStatus, setSelectedNewStatus] = useState(null);
  const [statusChangeReason, setStatusChangeReason] = useState("");
  const [statusChanging, setStatusChanging] = useState(false);

  // Detail drawer state
  const [detailPartner, setDetailPartner] = useState(null);

  const fetchPage = useCallback(
    async (pag = pagination) => {
      await list({ page: pag.current - 1, size: pag.pageSize });
    },
    [list, pagination],
  );

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => computeStats(partners), [partners]);

  const filteredPartners = useMemo(() => {
    if (!partners) return [];
    let result = partners;
    if (selectedStatus !== "ALL") {
      result = result.filter((p) => p.status === selectedStatus);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          (p.displayName || "").toLowerCase().includes(term) ||
          (p.email || "").toLowerCase().includes(term) ||
          (p.referralCode || "").toLowerCase().includes(term),
      );
    }
    return result;
  }, [partners, selectedStatus, searchTerm]);

  const handleRefresh = () => fetchPage();

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const handleEdit = useCallback((record) => {
    setEditing(record);
    setDrawerOpen(true);
  }, []);

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing?.id) {
        await update(editing.id, payload, actorId);
      } else {
        await create(payload, actorId);
      }
      setDrawerOpen(false);
      await fetchPage();
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel flow
  const promptCancel = useCallback((record) => {
    setCancelTarget(record);
    setCancelReason("");
  }, []);

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) return;
    setCancelling(true);
    try {
      await cancel(cancelTarget.id, cancelReason.trim(), actorId);
      setCancelTarget(null);
      setCancelReason("");
      await fetchPage();
    } finally {
      setCancelling(false);
    }
  };

  // Status change flow
  const promptStatusChange = useCallback((record) => {
    setStatusChangeTarget(record);
    setSelectedNewStatus(null);
    setStatusChangeReason("");
  }, []);

  const handleConfirmStatusChange = async () => {
    if (!statusChangeTarget || !selectedNewStatus) return;
    if (selectedNewStatus === "SUSPENDED" && !statusChangeReason.trim()) return;
    setStatusChanging(true);
    try {
      await update(
        statusChangeTarget.id,
        { status: selectedNewStatus, statusReason: statusChangeReason.trim() },
        actorId,
      );
      setStatusChangeTarget(null);
      setSelectedNewStatus(null);
      setStatusChangeReason("");
      await fetchPage();
    } finally {
      setStatusChanging(false);
    }
  };

  const handleTableChange = (pag) => {
    setPagination(pag);
    fetchPage(pag);
  };

  const columns = useMemo(
    () => [
      {
        title: "Display Name",
        dataIndex: "displayName",
        key: "displayName",
        width: 180,
        render: (text) => text || "-",
        sorter: (a, b) => (a.displayName || "").localeCompare(b.displayName || ""),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 220,
        render: (text) => text || "-",
        ellipsis: true,
      },
      {
        title: "Referral Code",
        dataIndex: "referralCode",
        key: "referralCode",
        width: 130,
        render: (text) =>
          text ? (
            <SubtleBadge bg="light" className="fw-bold" style={{ fontSize: "0.7rem" }}>
              {text}
            </SubtleBadge>
          ) : (
            "-"
          ),
      },
      {
        title: "Type",
        dataIndex: "partnerType",
        key: "partnerType",
        width: 120,
        render: (type) =>
          type ? (
            <SubtleBadge bg="info" style={{ fontSize: "0.7rem" }}>
              {PARTNER_TYPE_LABEL[type] || type}
            </SubtleBadge>
          ) : (
            "-"
          ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        align: "center",
        width: 130,
        render: (value) => {
          const status = value || "PENDING";
          const color = STATUS_BADGE_COLORS[status] || "secondary";
          return (
            <SubtleBadge
              bg={color}
              className="fw-bold"
              style={{
                borderRadius: "4px",
                padding: "2px 6px",
                fontSize: "0.7rem",
                textTransform: "uppercase",
              }}
            >
              {status.replaceAll("_", " ")}
            </SubtleBadge>
          );
        },
        sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        align: "center",
        width: 180,
        render: (v) => formatDateLong(v),
        sorter: (a, b) =>
          new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime(),
      },
      {
        title: "Actions",
        key: "actions",
        width: 90,
        render: (_, record) => {
          const items = [
            { key: "edit", label: "Edit" },
            { type: "divider" },
            ...(record.status !== "CANCELLED"
              ? [
                  {
                    key: "changeStatus",
                    label: "Change Status",
                  },
                ]
              : []),
            ...(record.status !== "CANCELLED"
              ? [
                  {
                    key: "cancel",
                    label: <span className="text-danger">Cancel Partner</span>,
                    danger: true,
                  },
                ]
              : []),
          ];

          return (
            <span
              className="action-dropdown"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              <AntDropdown
                trigger={["click"]}
                placement="bottomRight"
                menu={{
                  items,
                  onClick: ({ key, domEvent }) => {
                    domEvent?.stopPropagation?.();
                    if (key === "edit") handleEdit(record);
                    if (key === "changeStatus") promptStatusChange(record);
                    if (key === "cancel") promptCancel(record);
                  },
                }}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<EllipsisOutlined />}
                  onClick={(e) => e.stopPropagation()}
                />
              </AntDropdown>
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [promptStatusChange, promptCancel, handleEdit],
  );

  return (
    <>
      <WidgetSectionTitle
        className="mb-3 mt-4"
        title="Business Growth Partners"
        icon="handshake"
        subtitle="Manage and review growth partner accounts."
        transform="shrink-2"
      />

      {/* Search & Filter */}
      <Row className="mb-3 g-3 align-items-center">
        <Col xs={24} md={16} lg={18}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search by name, email or referral code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size="large"
              style={{
                borderRadius: 12,
                height: 30,
                flex: "1 1 300px",
                maxWidth: 300,
              }}
            />
            <Select
              size="large"
              value={selectedStatus}
              onChange={(value) => {
                setSelectedStatus(value);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
              options={STATUS_FILTER_OPTIONS}
              style={{ minWidth: 180, height: 28 }}
            />
          </div>
        </Col>
        <Col xs={24} md={8} lg={6} className="text-end">
          <div className="d-flex align-items-center justify-content-end">
            <IconButton
              size="sm"
              icon="plus"
              transform="shrink-3"
              onClick={handleCreate}
              type="success"            >
              <span className="ms-1">Add</span>
            </IconButton>
          </div>
        </Col>
      </Row>

      {/* Table */}
      {loading && partners.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner animation="border" variant="success" />
        </div>
      ) : filteredPartners.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty description="No growth partners found" />
        </div>
      ) : (
        <div style={{ width: "100%", overflowX: "hidden" }}>
          <Table
            rowKey="id"
            dataSource={filteredPartners}
            columns={columns}
            size="small"
            bordered
            className="custom-table"
            tableLayout="fixed"
            scroll={{ x: 1100 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: page.totalElements,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 30, 50, 100],
              onChange: (p, size) => {
                setPagination({ current: p, pageSize: size });
              },
            }}
            onRow={(record) => ({
              onClick: (e) => {
                if (e?.target?.closest?.(".action-dropdown, .ant-dropdown, .ant-dropdown-trigger")) return;
                setDetailPartner(record);
              },
              style: { cursor: "pointer" },
            })}
          />
        </div>
      )}

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      {/* Create/Edit Drawer — overlay only, does not push sidebar */}
      <BusinessGrowthPartnerDrawer
        open={drawerOpen}
        editing={editing}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
      />

      {/* Cancel Confirmation Modal */}
      <Modal
        title="Cancel Growth Partner?"
        open={Boolean(cancelTarget)}
        onOk={handleConfirmCancel}
        onCancel={() => {
          setCancelTarget(null);
          setCancelReason("");
        }}
        okText="Cancel Partner"
        okButtonProps={{ danger: true, loading: cancelling, disabled: !cancelReason.trim() }}
        cancelButtonProps={{ disabled: cancelling }}
        zIndex={1060}
      >
        <p>
          This will cancel{" "}
          <strong>{cancelTarget?.displayName || cancelTarget?.referralCode}</strong>.
          Their status will change to <strong>CANCELLED</strong>.
        </p>
        <div style={{ marginTop: 16 }}>
          <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
            Cancellation Reason <span style={{ color: "#dc3545" }}>*</span>
          </label>
          <TextArea
            rows={3}
            placeholder="Explain why this partner is being cancelled..."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </div>
      </Modal>

      {/* Status Change Modal */}
      <Modal
        title=""
        open={Boolean(statusChangeTarget)}
        onOk={handleConfirmStatusChange}
        onCancel={() => {
          setStatusChangeTarget(null);
          setSelectedNewStatus(null);
          setStatusChangeReason("");
        }}
        okText={selectedNewStatus ? `Change to ${selectedNewStatus}` : "Change"}
        okButtonProps={{
          loading: statusChanging,
          disabled:
            !selectedNewStatus ||
            (selectedNewStatus === "SUSPENDED" && !statusChangeReason.trim()),
        }}
        zIndex={1060}
      >
        <p style={{ marginBottom: 16 }}>
          Change status of{" "}
          <strong>
            {statusChangeTarget?.displayName || statusChangeTarget?.referralCode}
          </strong>
          . Current status:{" "}
          <strong>{statusChangeTarget?.status}</strong>
        </p>
        <Select
          style={{ width: "100%" }}
          placeholder="Select new status"
          value={selectedNewStatus}
          onChange={setSelectedNewStatus}
          options={[
            { value: "ACTIVE", label: "Active" },
            { value: "INACTIVE", label: "Inactive" },
            { value: "SUSPENDED", label: "Suspended" },
          ]}
        />
        <div style={{ marginTop: 16 }}>
          <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
            Reason for change
            {selectedNewStatus === "SUSPENDED" && (
              <span style={{ color: "#dc3545" }}> *</span>
            )}
          </label>
          <TextArea
            rows={3}
            placeholder={
              selectedNewStatus === "SUSPENDED"
                ? "Explain why this partner is being suspended..."
                : "Optional reason for the status change"
            }
            value={statusChangeReason}
            onChange={(e) => setStatusChangeReason(e.target.value)}
          />
        </div>
      </Modal>

      {/* Detail Drawer — opens on row click */}
      <BusinessGrowthPartnerDetailDrawer
        open={Boolean(detailPartner)}
        partner={detailPartner}
        onClose={() => setDetailPartner(null)}
      />
    </>
  );
}
